import React, { useCallback, useEffect, useRef, useState } from 'react';
import H from '@here/maps-api-for-javascript';
import { DEFAULT_MAP_ZOOM, HERE_MAP_KEY } from './constants';
import { toGroundPoint } from './toGroundPoint';

type MapProps = {
  markers?: H.geo.IPoint[];
  icon?: string;
  circle?: { center: H.geo.IPoint; radius?: number };
  zoom?: number;
  bounds?: H.geo.Rect;
};

interface MapOverlay {
  path: string;
  bounds: {
    north: number;
    east: number;
    south: number;
    west: number;
  };
}

export const FullscreenMap = ({
  markers,
  icon,
  circle,
  zoom,
  bounds,
}: MapProps) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);

  useEffect(() => {
    // Check if the map object has already been created
    if (!map.current && mapRef.current) {
      // Create a platform object with the API key and useCIT option
      const platform = new H.service.Platform({ apikey: HERE_MAP_KEY });
      // Obtain the default map types from the platform object:
      const defaultLayers: any = platform.createDefaultLayers({ pois: true });
      // Create a new map instance with the Tile layer, center and zoom level
      // Instantiate (and display) a map:
      const newMap = new H.Map(mapRef.current, defaultLayers.vector.normal.map);

      // add a resize listener to make sure that the map occupies the whole container
      window.addEventListener('resize', () => newMap.getViewPort().resize());

      // Add panning and zooming behavior to the map
      const behavior = new H.mapevents.Behavior(
        new H.mapevents.MapEvents(newMap),
      );
      behavior.enable();
      // create the default UI component, for displaying bubbles
      H.ui.UI.createDefault(newMap, defaultLayers);

      // Set the map object to the reference
      map.current = newMap;
      setMapLoaded(true);
      setTimeout(() => {
        newMap.getViewPort().resize();
        newMap.getViewModel().setLookAtData({
          tilt: 0,
          heading: 150,
        });
      }, 500);
    }
  }, []);

  useEffect(() => {
    // Check if the map object has been created
    if (!mapLoaded) return;

    const currentMap: H.Map = map.current;
    // set map zoom and bounds
    currentMap.getViewModel().setLookAtData({
      zoom: zoom || DEFAULT_MAP_ZOOM,
      bounds: bounds,
    });
  }, [bounds, mapLoaded, zoom]);

  useEffect(() => {
    // Check if the map object has been created
    if (!mapLoaded || !markers) return;

    const currentMap: H.Map = map.current;
    // Remove all markers from the map
    const objects = currentMap.getObjects();
    objects.forEach((object) => {
      if (object.getData() === 'markers') {
        currentMap.removeObject(object);
      }
    });

    // Create a group object to hold the markers
    const group = new H.map.Group({ data: 'markers' });

    // Create a new marker for each marker in the markers array
    const markerIcon = new H.map.Icon(icon || `/images/marker.png`, {
      size: { w: 32, h: 32 },
      anchor: { x: 16, y: 16 }, // Center the icon at the marker position
    });
    const newMarker = new H.map.Marker(
      new H.geo.MultiPoint(markers.map(toGroundPoint)),
      {
        data: 'marker',
        icon: markerIcon,
      },
    );
    // Add the marker to the map
    group.addObject(newMarker);
    // Add the group of markers to the map
    currentMap.addObject(group);
  }, [icon, mapLoaded, markers]);

  useEffect(() => {
    // Check if the map object has been created
    if (!mapLoaded || !circle) return;

    const currentMap: H.Map = map.current;
    // Remove all circles from the map
    const objects = currentMap.getObjects();
    objects.forEach((object) => {
      if (object.getData() === 'circles') {
        currentMap.removeObject(object);
      }
    });

    // Create a group object to hold the circles
    const group = new H.map.Group({ data: 'circles' });

    // Create a new circle with the given center and radius
    group.addObject(
      new H.map.Circle(
        // The central point of the circle
        toGroundPoint(circle.center),
        // The radius of the circle in meters
        circle.radius || 1000,
        {
          style: {
            strokeColor: 'rgba(55, 85, 170, 0.6)', // Color of the perimeter
            lineWidth: 0,
            fillColor: 'rgba(83, 85, 92, 0.6)', // Color of the circle
          },
        } as any, // Type assertion to avoid type errors
      ),
    );

    // Add the group of circles to the map
    currentMap.addObject(group);
  }, [circle, mapLoaded]);

  // Function to add image overlay to the map
  const addImageOverlay = useCallback((items: MapOverlay[]) => {
    const currentMap: H.Map = map.current;
    if (!currentMap) return;

    // Remove existing image overlay if any
    const objects = currentMap.getObjects();
    objects.forEach((object) => {
      if (object.getData() === 'images') {
        currentMap.removeObject(object);
      }
    });

    // Create a group object to hold the image overlays
    const group = new H.map.Group({ data: 'images' });
    items.forEach((item) => {
      // Create a bounding box for the image
      const boundingBox = new H.geo.Rect(
        item.bounds.north,
        item.bounds.west,
        item.bounds.south,
        item.bounds.east,
      );

      // Create a new image overlay
      const overlay = new H.map.Overlay(
        boundingBox,
        item.path,
        // H.map.Overlay doesn't accept options like opacity in this API version
      );

      group.addObject(overlay);
    });
    // Add the overlay to the map
    currentMap.addObject(group);
  }, []);

  // Handle image overlay - hardcoded
  useEffect(() => {
    if (!mapLoaded) return;

    // Hardcoded image overlay configuration
    const imageOverlayConfig: MapOverlay[] = [
      {
        path: '/images/southampton-map.png',
        bounds: {
          north: 50.89698978462004, // Top latitude
          south: 50.8876997312453, // Bottom latitude
          east: -1.3892245279002877, // Right longitude
          west: -1.3982925596509446, // Left longitude
        },
      },
      {
        path: '/images/killing-map.png',
        bounds: {
          north: 53.65698, // Top latitude
          south: 53.64874, // Bottom latitude
          east: -0.23128, // Right longitude
          west: -0.24642, // Left longitude
        },
      },
      {
        path: '/images/thorne-map.png',
        bounds: {
          north: 53.62360582452899, // Top latitude
          south: 53.61695895319273, // Bottom latitude
          east: -0.9660601625563685, // Right longitude
          west: -0.9768633527685173, // Left longitude
        },
      },
    ];

    addImageOverlay(imageOverlayConfig);
  }, [mapLoaded, addImageOverlay]);

  // Return a div element to hold the map
  return <div className="h-100 w-100" ref={mapRef} />;
};
